import {Component, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {of} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {AnalyticsService} from '../../shared/analytics.service';
import {ApiService, HttpError} from '../../shared/api.service';
import {AppStateService} from '../../shared/app-state.service';
import {AuthService} from '../../shared/auth.service';
import {ModalComponent} from '../../shared/modal/modal.component';
import {NavigationService} from '../../shared/navigation.service';
import {StorageService} from '../../shared/storage.service';

const TERMS_ACCEPTED_KEY = 'termsAccepted';

@Component({
  selector: 'login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss']
})
export class LoginPageComponent implements OnInit {

  @ViewChild('verifyEmail') verifyEmailModal: ModalComponent;

  termsAccepted = false;
  loginForm: FormGroup;
  reset = false;

  returnTo: string | null = null;
  position: string | null = null;
  registered = false;

  loggingIn = false;
  submitted = false;
  submitError: string | null = null;
  submitErrorCode: string | null = null;

  constructor(private FormBuilder: FormBuilder,
              private AppState: AppStateService,
              private Storage: StorageService,
              private Analytics: AnalyticsService,
              private Api: ApiService,
              private Auth: AuthService,
              private Router: Router,
              private route: ActivatedRoute,
              private Navigation: NavigationService) {
  }

  ngOnInit() {
    if (this.Auth.isFullyAuthenticated()
      // Allow timecard signers to log in
      && this.Auth.getAuthType() !== 'timecard') {
      this.AppState.loading$.next(true);
      this.postAuthentication();
    } else if (this.route.snapshot.queryParams.autotest) {
      // Skip MA:T login
      this.Storage.put('autotest', true);
    } else if (environment.flags.matLogin) {
      this.AppState.loading$.next(true);
      window.location.href = `${environment.matHost}/myaquent?PROC=AWUIDrawMatLogin`;
    }

    const params = this.route.snapshot.params;
    this.returnTo = params['returnTo'] || null;
    this.position = params['position'] || null;
    this.reset = !!params['reset'];
    this.registered = !!params['registered'];

    this.termsAccepted = !!this.Storage.get(TERMS_ACCEPTED_KEY);
    this.loginForm = this.FormBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      ...(this.termsAccepted ? {} : {agreeTerms: [false, Validators.requiredTrue]})
    });
  }

  login() {
    this.submitted = true;

    if (this.loginForm.valid) {
      this.loggingIn = true;
      this.Storage.put(TERMS_ACCEPTED_KEY, true);
      this.Analytics.trackEvent('Log in submitted', {category: 'Action'});

      this.Api.authenticate(this.username.value, this.password.value)
        .pipe(
          catchError((err: HttpError) => {
            if ((err.status === 400 && err.code === 'invalid_grant')
              || (err.status === 401 && err.code === 'unauthorized')) {
              const credentialsError = () => {
                this.loggingIn = false;
                this.submitError = 'incorrect-credentials';
                this.Analytics.trackEvent('Log in incorrect credentials', {category: 'Action'});
              };
              // Attempt to resend verification email if user is "disabled"
              if (err.data && err.data['error_description'] && err.data['error_description'].includes('disabled')) {
                this.Api.resendVerificationEmail(this.username.value, this.password.value)
                  .pipe(
                    catchError<any, null>((err): null => {
                      credentialsError();
                      throw err;
                    }),
                    tap(res => {
                      if (res && res.success === 'email_sent') {
                        this.verifyEmailModal.show();
                      }
                    })
                  )
                  .subscribe(msg => {
                    if (msg === 'already_verified') {
                      this.submitError = 'unauthorized';
                    }
                    this.loggingIn = false;
                  });
              } else {
                credentialsError();
              }
            } else {
              this.loggingIn = false;
              this.submitError = 'server-error';
              this.submitErrorCode = err.code;
              this.Analytics.trackEvent('Log in failed', {category: 'Error', code: err.code});
            }
            return of(null);
          }),
          switchMap(() => this.Api.refreshUser())
        )
        .subscribe(() => {
          if (this.Auth.isFullyAuthenticated()) {
            this.Analytics.trackEvent('Log in succeeded');
            this.postAuthentication();
          }
        }, err => {
          this.loggingIn = false;
          this.submitError = err === 'unauthorized' ? 'unauthorized' : 'incorrect-credentials';
        });
    }
  }

  get username() {
    return this.loginForm.get('username');
  }

  get password() {
    return this.loginForm.get('password');
  }

  get agreeTerms() {
    return this.loginForm.get('agreeTerms');
  }

  private postAuthentication() {
    if (this.returnTo) {
      this.Router.navigateByUrl(this.returnTo);
    } else {
      this.Navigation.goHome();
    }
  }
}
